<template>
	<NuxtLayout
		name="detail"
		title="Functional Strength"
		description="Tijdens onze Functional Strength lessen werk je aan kracht, stabiliteit en controle over functionele bewegingen. Een resultaatgerichte training voor iedereen die sterker wil worden op een manier die direct toepasbaar is in het dagelijks leven."
		:breadcrumbs
		:button="{
			text: 'Informatie aanvragen',
			to: {
				url: '#aanmelden',
				title: 'Small Group Training informatie aanvragen',
			},
			iconRight: 'fa-envelope',
		}"
	>
		<div class="container max-w-screen-md flex flex-col gap-24 md:gap-36 py-36">
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Wat is Functional Strength?
				</AtomTitle>
				<p>
					Functional Strength is een krachtige en resultaatgerichte trainingsvorm waarin je werkt aan het opbouwen van functionele spierkracht. De training is gericht op grote, samengestelde bewegingen die jouw lichaam sterker maken op een manier die direct toepasbaar is in het dagelijks leven. Functional Strength is onderdeel van het Small Group Training lidmaatschap van Fit Hier en perfect voor wie sterker, stabieler en blessurebestendiger wil worden.
				</p>
			</div>
		</div>
		<div class="container grid sm:grid-cols-2 gap-8">
			<AtomImage
				src="/img/fitness/slee_05.jpg"
				alt="Battle Rope workout in Small Group Training"
				img-class="rounded aspect-video object-cover"
				:width="1220"
				:height="686"
			/>
			<AtomImage
				src="/img/fitness/squat-rack_01.jpg"
				alt="Airbike en Roeien in Small Group Training"
				img-class="rounded aspect-video object-cover"
				:width="1220"
				:height="686"
				fit="cover"
			/>
		</div>
		<div class="container max-w-screen-md flex flex-col gap-24 md:gap-36 py-36">
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Voor wie is deze training geschikt?
				</AtomTitle>
				<p>
					Functional Strength is geschikt voor iedereen met een basisconditie die serieus wil werken aan krachtopbouw. De training is opgebouwd rondom progressieve krachtblokken en biedt ruimte voor zowel beginnende sporters als ervaren krachtsporters. Onze trainers helpen je veilig en technisch verantwoord sterker worden, stap voor stap.
				</p>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Wat kun je verwachten?
				</AtomTitle>
				<p>
					Elke training duurt 60 minuten en vindt plaats in een kleine groep van maximaal zes deelnemers. Je traint onder begeleiding van een professionele coach die je techniek bewaakt en je uitdaagt om het maximale uit jezelf te halen.
				</p>
				<p>We starten met een actieve warming-up om je lichaam voor te bereiden op de krachtinspanningen die volgen. Na de training sluiten we af met een stretchsessie die gericht is op cooling-down, mobiliteit en herstel.</p>
				<p>De trainingen bestaan uit onder andere:</p>
				<MoleculeIconList
					class="md:columns-2"
					:items="[
						'Krachttraining met halters, barbells en kettlebells',
						'Oefeningen zoals squats, deadlifts, presses, lunges',
						'Core stability en lichaamsbeheersing',
						'Progressieve belasting en techniekopbouw',
					]"
				/>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					De voordelen van Functional Strength
				</AtomTitle>
				<MoleculeIconList
					class="md:columns-2"
					:items="[
						'Verhoogt spierkracht en stabiliteit',
						'Verbetert lichaamshouding en bewegingscontrole',
						'Helpt blessures voorkomen',
						'Ondersteunt sportprestaties en dagelijks functioneren',
						'Kleine groepen met persoonlijke coaching',
					]"
				/>
			</div>
			<div class="flex justify-center gap-8">
				<AtomButton
					size="lg"
					:variant="ButtonVariant.Green"
					icon-right="fa-envelope"
					text="Informatie aanvragen"
					:to="{
						url: '#aanmelden',
						title: 'Small Group Training informatie aanvragen',
					}"
					@click="trackButtonClick('Page_FunctionalStrength_CTA_Lead')"
				/>
				<AtomButton
					size="lg"
					:variant="ButtonVariant.DarkBorder"
					icon-right="fa-bolt"
					text="Meedoen met een gratis training"
					:to="{
						url: '/small-group-training/aanmelden-proefles',
						title: 'Aanmelden voor een Small Group Training proefles',
					}"
				/>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Waarom trainen bij Fit Hier?
				</AtomTitle>
				<p>
					Bij Fit Hier in Uden train je in een exclusieve setting, zonder drukte of afleiding. Dankzij onze kleinschalige aanpak en persoonlijke begeleiding krijg je de aandacht die nodig is om gecontroleerd en doelgericht sterker te worden. Onze trainers begeleiden je bij elke stap en zorgen dat je technisch correct en veilig traint.
				</p>
			</div>
			<div class="flex flex-col gap-4">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Informatie aanvragen
				</AtomTitle>
				<p>
					Wil je meer weten over het Small Group Training lidmaatschap? Vraag het informatiepakket aan en je ontvangt binnen enkele minuten alle details over de trainingen, het rooster en de tarieven per mail.
				</p>
			</div>
			<AtomCard
				id="aanmelden"
				class="p-12 scroll-mt-36"
			>
				<OrganismFormLeadSgt />
			</AtomCard>

			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="text-center"
				>
					Ervaringen van leden
				</AtomTitle>
				<OrganismReviewSlideshow :reviews="reviews" />
			</div>

			<div class="flex flex-col gap-16">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="text-center"
				>
					Veelgestelde vragen
				</AtomTitle>
				<OrganismFaq
					:items="functionalStrengthFaqItems"
				/>
			</div>
		</div>
	</NuxtLayout>
</template>

<script setup lang="ts">
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import reviews from '#fit/core/consts/reviews';
import { functionalStrengthFaqItems } from '#fit/core/consts/faqItems';

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'Small Group Training',
		to: {
			url: '/small-group-training',
			title: 'Small Group Training',
		},
	},
	{
		text: 'Functional Strength',
		to: {
			url: '/small-group-training/functional-strength-krachttraining',
			title: 'Functional Strength',
		},
	},
];

useSeoMeta({
	title: 'Functional Strength - Krachttraining Uden | Fit Hier',
	ogTitle: 'Small Group Krachttraining bij Fit Hier Uden',
	description: 'Vergroot je kracht met Functional Strength Small Group Training in Uden. Maximaal 6 deelnemers, persoonlijke coaching. Boek je gratis proefles!',
});

const { trackButtonClick } = useEventTracking();
</script>
